import { apiAuth, apiResponseType } from ".";

// ============== REGRAS DE COMISSAO ==============

export const getSellerCommissionRules = (
  page: number = 1,
  filters: Record<string, string | number | boolean> = {}
): apiResponseType => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value.toString());
    }
  });

  return apiAuth.get(`seller-commission/rules?${queryParams.toString()}`);
};

export const getSellerCommissionRule = (uuid: string): apiResponseType => {
  return apiAuth.get(`seller-commission/rules/${uuid}`);
};

export interface CommissionTier {
  tier_number: number;
  min_value: number;
  max_value: number | null;
  rate_below_goal: number;
  rate_at_goal: number;
  rate_above_goal: number;
}

export interface CommissionRulePayload {
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  apply_to_all_unities: boolean;
  unity_ids?: number[];
  tiers: CommissionTier[];
}

export const createSellerCommissionRule = (
  data: CommissionRulePayload
): apiResponseType => {
  return apiAuth.post("seller-commission/rules", data);
};

export const updateSellerCommissionRule = (
  uuid: string,
  data: CommissionRulePayload
): apiResponseType => {
  return apiAuth.put(`seller-commission/rules/${uuid}`, data);
};

export const deleteSellerCommissionRule = (uuid: string): apiResponseType => {
  return apiAuth.delete(`seller-commission/rules/${uuid}`);
};

// ============== CALCULOS/ACOMPANHAMENTOS ==============

export const getSellerCommissionCalculations = (
  page: number = 1,
  filters: Record<string, string | number> = {}
): apiResponseType => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value.toString());
    }
  });

  return apiAuth.get(`seller-commission/calculations?${queryParams.toString()}`);
};

export const getSellerCommissionCalculation = (uuid: string): apiResponseType => {
  return apiAuth.get(`seller-commission/calculations/${uuid}`);
};

export interface GenerateCalculationsPayload {
  period_start: string;
  period_end: string;
  unity_id?: number;
  goal_status: "below_goal" | "at_goal" | "above_goal";
}

export const generateSellerCommissionCalculations = (
  data: GenerateCalculationsPayload
): apiResponseType => {
  return apiAuth.post("seller-commission/calculations/generate", data);
};

export const approveSellerCommissionCalculation = (
  uuid: string
): apiResponseType => {
  return apiAuth.put(`seller-commission/calculations/${uuid}/approve`);
};

export const markSellerCommissionCalculationAsPaid = (
  uuid: string
): apiResponseType => {
  return apiAuth.put(`seller-commission/calculations/${uuid}/pay`);
};

// ============== COMISSÃO DO VENDEDOR (DASHBOARD) ==============

export interface MyCommissionResponse {
  current_commission: number;
  previous_commission: number;
  percent_change: number;
  daily_average: number;
  projection: number;
  total_sales: number;
  rate_applied: number;
  tier_number: number | null;
  goal_status: "below_goal" | "at_goal" | "above_goal";
  goal_value: number;
  goal_progress: number;
  month: string;
  month_name: string;
}

export const getMyCommission = (period?: string, startDate?: string, endDate?: string): apiResponseType => {
  const params = new URLSearchParams();
  if (period) params.append("period", period);
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  const queryString = params.toString();
  return apiAuth.get(`seller-commission/calculations/my-commission${queryString ? `?${queryString}` : ""}`);
};

// ============== CÁLCULO EM TEMPO REAL ==============

export interface RealTimeFilters {
  period_start?: string;
  period_end?: string;
  unity_id?: number;
  rule_id?: number;
}

export interface RealTimeCommissionData {
  seller_id: number;
  user_id: number;
  seller_name: string;
  unity_id: number;
  unity_name: string;
  total_sales: number;
  tier_number: number;
  rate_applied: number;
  commission_value: number;
  goal_status: "below_goal" | "at_goal" | "above_goal";
  rule_name: string;
}

export interface RealTimeMetrics {
  total_commission: number;
  total_sellers: number;
  avg_commission: number;
  total_sales: number;
  top_seller: { name: string; value: number } | null;
}

export interface RealTimeResponse {
  data: RealTimeCommissionData[];
  metrics: RealTimeMetrics;
  period: { start: string; end: string };
}

export const getCommissionsRealTime = (
  filters: RealTimeFilters = {}
): apiResponseType => {
  const queryParams = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value.toString());
    }
  });

  const queryString = queryParams.toString();
  return apiAuth.get(`seller-commission/calculations/real-time${queryString ? `?${queryString}` : ""}`);
};

// ============== EXPORTAÇÃO ==============

export interface ExportCommissionData {
  data: RealTimeCommissionData[];
  metrics: RealTimeMetrics;
  period: { start: string; end: string };
}

export const exportCommissionsExcel = (
  payload: ExportCommissionData
): Promise<Blob> => {
  return apiAuth
    .post("seller-commission/calculations/export-excel", payload, {
      responseType: "blob",
    })
    .then((response: any) => response.data);
};

export const exportCommissionsPdf = (
  payload: ExportCommissionData
): Promise<Blob> => {
  return apiAuth
    .post("seller-commission/calculations/export-pdf", payload, {
      responseType: "blob",
    })
    .then((response: any) => response.data);
};
